import { ApolloCache, ApolloError } from '@apollo/client';

import { describeRateLimitRejection, getRateLimitRejection } from '../../../../common/rateLimit';
import { countryName } from '../../contacts/contactList';
import { ContactDetail } from '../contactDetail';

/**
 * Editing one person on their page (card 21.5): the logic, with no React.
 * What the forms start from, what they send (only what changed), the person's
 * emails and numbers as rows (an app member's own address and number are not
 * rows in the CRM yet, and are shown as one), and errors in words.
 */

export type PhoneType = 'MOBILE' | 'HOME' | 'WORK' | 'FAX';

export const PHONE_TYPES: Array<{ value: PhoneType; label: string }> = [
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'HOME', label: 'Home' },
  { value: 'WORK', label: 'Work' },
  { value: 'FAX', label: 'Fax' },
];

export const phoneTypeWord = (type: string | null | undefined) =>
  PHONE_TYPES.find((t) => t.value === type)?.label ?? (type ? type.toLowerCase() : 'Number');

/* ------------------------------------------------------------------ *
 * Name, place and outreach (crmUpdateContact)
 * ------------------------------------------------------------------ */

export interface DetailsForm {
  prefix: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  countryCode: string;
  region: string;
  city: string;
  /** An admin's id, or '' for none. */
  pointPerson: string;
  /** '1'..'5', or '' for none. */
  supportLevel: string;
  stage: string;
}

export const detailsFormOf = (c: ContactDetail): DetailsForm => ({
  prefix: c.prefix ?? '',
  firstName: c.firstName ?? '',
  middleName: c.middleName ?? '',
  lastName: c.lastName ?? '',
  suffix: c.suffix ?? '',
  countryCode: c.countryCode ?? '',
  region: c.region ?? '',
  city: c.city ?? '',
  pointPerson: c.owner ? String(c.owner.id) : '',
  supportLevel: c.supportLevel != null ? String(c.supportLevel) : '',
  stage: c.lifecycleStage ?? '',
});

const tidy = (value: string) => value.trim().replace(/\s+/g, ' ');

/**
 * crmUpdateContact's `change`: only the fields that differ from the person as
 * loaded, emptied ones as null (which clears them). Empty when nothing changed.
 */
export const detailsChange = (form: DetailsForm, c: ContactDetail): Record<string, string | number | null> => {
  const out: Record<string, string | number | null> = {};
  const text = (key: keyof DetailsForm, current: string | null) => {
    const value = tidy(form[key]);
    if (value !== (current ?? '')) out[key] = value || null;
  };
  text('prefix', c.prefix);
  text('firstName', c.firstName);
  text('middleName', c.middleName);
  text('lastName', c.lastName);
  text('suffix', c.suffix);
  const country = tidy(form.countryCode).toUpperCase();
  if (country !== (c.countryCode ?? '')) out.countryCode = country || null;
  text('region', c.region);
  text('city', c.city);
  const owner = form.pointPerson ? Number(form.pointPerson) : null;
  if (owner !== (c.owner?.id ?? null)) out.pointPerson = owner;
  const level = form.supportLevel ? Number(form.supportLevel) : null;
  if (level !== c.supportLevel) out.supportLevel = level;
  const stage = tidy(form.stage);
  if (stage !== (c.lifecycleStage ?? '')) out.stage = stage || null;
  return out;
};

/** "Abena Mensah, Kumasi, Ghana": what the dialog's preview line says. */
export const placeWords = (form: Pick<DetailsForm, 'city' | 'region' | 'countryCode'>) =>
  [tidy(form.city), tidy(form.region), form.countryCode ? countryName(form.countryCode.toUpperCase()) : ''].filter(Boolean).join(', ');

/* ------------------------------------------------------------------ *
 * Emails and numbers as rows
 * ------------------------------------------------------------------ */

export interface EmailItem {
  /** Null for an app member's own address, not a CRM row yet: the API takes it with no emailId. */
  id: number | null;
  email: string;
  isPrimary: boolean;
  bad: boolean;
  bouncedAt: string | null;
  fromApp: boolean;
}

/** Their addresses, the main one first. */
export const emailItems = (c: ContactDetail): Array<EmailItem> => {
  const rows: Array<EmailItem> = c.emails.map((e) => ({
    id: e.id,
    email: e.email,
    isPrimary: e.email === c.primaryEmail,
    bad: e.isBad || e.isInvalid,
    bouncedAt: e.bouncedAt,
    fromApp: false,
  }));
  if (c.primaryEmail && !rows.some((r) => r.email === c.primaryEmail)) {
    rows.push({ id: null, email: c.primaryEmail, isPrimary: true, bad: c.emailIsBad, bouncedAt: c.emailBouncedAt, fromApp: true });
  }
  return rows.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
};

export interface PhoneItem {
  /** Null for an app member's own number, not a CRM row yet: the API takes it with no phoneId. */
  id: number | null;
  type: string | null;
  /** What to show: the E.164 number, or NationBuilder's text when it could not be read. */
  text: string;
  e164: string | null;
  /** The number calls go to. */
  isPrimary: boolean;
  isBad: boolean;
  fromApp: boolean;
}

const ORDER = ['MOBILE', 'HOME', 'WORK', 'FAX'];

/** Their numbers, the one calls go to first, then mobile, home, work, fax. */
export const phoneItems = (c: ContactDetail): Array<PhoneItem> => {
  const rows: Array<PhoneItem> = c.phones.map((p) => ({
    id: p.id,
    type: p.type,
    text: p.e164 ?? p.rawDisplay ?? '',
    e164: p.e164,
    isPrimary: p.isPrimary || (p.e164 != null && p.e164 === c.primaryPhone && !c.phones.some((q) => q.isPrimary)),
    isBad: p.isBad,
    fromApp: false,
  }));
  if (c.primaryPhone && !rows.some((r) => r.e164 === c.primaryPhone)) {
    rows.push({ id: null, type: null, text: c.primaryPhone, e164: c.primaryPhone, isPrimary: true, isBad: false, fromApp: true });
  }
  return rows.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || ORDER.indexOf(a.type ?? '') - ORDER.indexOf(b.type ?? ''));
};

/** A number that can be called: read as E.164, not a fax. */
export const callableItem = (p: Pick<PhoneItem, 'e164' | 'type'>) => p.e164 != null && p.type !== 'FAX';

/** Their main number is marked bad: the call and text gates are closed by it (card 21.5). */
export const mainNumberBad = (c: Pick<ContactDetail, 'primaryPhone' | 'phones'>) =>
  c.primaryPhone != null && c.phones.some((p) => p.e164 === c.primaryPhone && p.isBad);

/**
 * Where calls go once this number is marked bad, as the API will choose: the
 * first good number of mobile, home and work. Null when none is left.
 */
export const nextGoodNumber = (c: ContactDetail, leaving: PhoneItem): PhoneItem | null =>
  phoneItems(c)
    .filter((p) => p !== leaving && (p.id === null || p.id !== leaving.id) && !p.isBad && callableItem(p))
    .sort((a, b) => ORDER.indexOf(a.type ?? 'MOBILE') - ORDER.indexOf(b.type ?? 'MOBILE'))[0] ?? null;

/** The types they have no number of yet: one number per type. */
export const freePhoneTypes = (c: Pick<ContactDetail, 'phones'>): Array<PhoneType> =>
  PHONE_TYPES.map((t) => t.value).filter((t) => !c.phones.some((p) => p.type === t));

/**
 * The number a call reached, for "mark it bad" after a wrong number: their main
 * one, as a row id (null for an app member's own number) and its text. Takes
 * the contact page's contact or the call queue's person.
 */
export const dialledNumber = (c: {
  primaryPhone: string | null;
  phones: ReadonlyArray<{ id: number; e164: string | null; isPrimary: boolean; isBad: boolean }>;
}): { id: number | null; text: string } | null => {
  if (!c.primaryPhone) return null;
  const held = c.phones.filter((p) => p.e164 === c.primaryPhone);
  const row = held.find((p) => p.isPrimary) ?? held[0];
  if (row?.isBad) return null;
  return { id: row ? row.id : null, text: c.primaryPhone };
};

/* ------------------------------------------------------------------ *
 * What the forms check before sending (the API checks again)
 * ------------------------------------------------------------------ */

export const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(value.trim());

/**
 * What the number field says under itself: how a national number will be read,
 * or why it cannot be. Null when there is nothing to say yet.
 */
export const numberHint = (value: string, countryCode: string | null): { text: string; error: boolean } | null => {
  const raw = value.trim();
  if (!raw) return null;
  if (!/^[+\d\s().\-/]+$/.test(raw)) return { text: 'Digits only, with + for the country code.', error: true };
  if (raw.startsWith('+') || raw.replace(/\D/g, '').startsWith('00')) return { text: 'Read as an international number.', error: false };
  if (!countryCode) return { text: 'Their country is not known: start with + and the country code.', error: true };
  return { text: `Read as a number in ${countryName(countryCode)}.`, error: false };
};

/* ------------------------------------------------------------------ *
 * Addresses
 * ------------------------------------------------------------------ */

export interface AddressForm {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
}

export const addressFormOf = (a?: Partial<ContactDetail['addresses'][number]> | null, c?: ContactDetail): AddressForm => ({
  address1: a?.address1 ?? '',
  address2: a?.address2 ?? '',
  city: a?.city ?? (a ? '' : c?.city ?? ''),
  state: a?.state ?? (a ? '' : c?.region ?? ''),
  zip: a?.zip ?? '',
  countryCode: a?.countryCode ?? (a ? '' : c?.countryCode ?? ''),
});

/** CrmContactAddressInput: empty lines left out, the country upper-cased. */
export const addressInput = (form: AddressForm) => {
  const out: Record<string, string> = {};
  (Object.keys(form) as Array<keyof AddressForm>).forEach((key) => {
    const value = tidy(form[key]);
    if (value) out[key] = key === 'countryCode' ? value.toUpperCase() : value;
  });
  return out;
};

export const addressReady = (form: AddressForm) => Boolean(tidy(form.address1) || tidy(form.city));

/** The place an address would give the person, in words, for "Also set their place". */
export const placeFromAddress = (form: AddressForm) =>
  placeWords({ city: form.city, region: form.state, countryCode: form.countryCode });

/* ------------------------------------------------------------------ *
 * After an edit, and when one fails
 * ------------------------------------------------------------------ */

/**
 * Drops what an edit can change from the cache: the person's timeline (a new
 * change entry), the contact list and its counts (their name, the gates), the
 * queue's summary (whether they can be called). Mounted queries refetch.
 */
export const forgetEditData = (cache: ApolloCache<unknown>) => {
  ['crmContacts', 'crmContactCounts', 'crmContactTimeline', 'crmCallQueueSummary', 'crmContactSearch'].forEach((fieldName) =>
    cache.evict({ id: 'ROOT_QUERY', fieldName }),
  );
  cache.gc();
};

/** An edit's failure in words, and the field it names when the API says so. */
export const describeEditError = (error: ApolloError | Error): { message: string; field?: string } => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return { message: describeRateLimitRejection(rejection) };
  const graphQL = (error as ApolloError).graphQLErrors?.[0];
  const extensions = (graphQL?.extensions ?? {}) as Record<string, unknown>;
  switch (extensions.code) {
    case 'CRM_INVALID_QUERY': {
      const field = String(extensions.field ?? '').split('.').pop();
      return { message: graphQL?.message ?? 'The API refused the change.', ...(field ? { field } : {}) };
    }
    case 'CRM_CONTACT_MERGED':
      return { message: `This record was merged into contact #${extensions.mergedIntoId}. Edit that contact instead.` };
    case 'CRM_CONTACT_NOT_FOUND':
      return { message: 'This contact no longer exists. Nothing was changed.' };
    case 'CRM_CONTACT_DETAIL_NOT_FOUND':
      return { message: 'Someone else removed or changed this a moment ago. Reload the page to see it as it is now.' };
    case 'CRM_NOTE_NOT_EDITABLE':
      return { message: graphQL?.message ?? 'This note cannot be changed.' };
    default:
      return { message: 'The change could not be saved. Check your connection and try again.' };
  }
};
